import {ApiClientError} from '@/api/client';
import {Link, useSearchParams} from 'react-router-dom';
import {TribeTaleMark} from '@/components/auth/TribeTaleMark';
import {authApi} from '@/api/auth';
import {getSafeReturnTo} from '@/lib/auth-return';
import {useForm} from 'react-hook-form';
import {useState} from 'react';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';

const forgotPasswordSchema = z.object({
    email: z.string().email('Invalid email address')
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
    const [searchParams] = useSearchParams();
    const returnTo = getSafeReturnTo(searchParams.get('returnTo'));
    const loginUrl = `/login?returnTo=${encodeURIComponent(returnTo)}`;
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: {errors}
    } = useForm<ForgotPasswordFormData>({
        resolver: zodResolver(forgotPasswordSchema)
    });

    const onSubmit = async (data: ForgotPasswordFormData) => {
        setError('');
        setSuccess(false);
        setIsLoading(true);

        try {
            await authApi.sendSignInLink(data.email, returnTo);
            setSuccess(true);
        } catch (err) {
            if (err instanceof ApiClientError) {
                setError(err.message);
            } else {
                setError('An unexpected error occurred. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    if (success) {
        return (
            <div style={{width: '100%'}}>
                <header style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    <TribeTaleMark size={56} />
                    <h1 style={{
                        fontSize: 30,
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        color: 'var(--tt-text-primary)',
                        textAlign: 'center',
                        margin: '22px 0 40px',
                        padding: 0
                    }}>
                        Check your email
                    </h1>
                </header>

                <p
                    style={{
                        fontSize: 15,
                        lineHeight: 1.5,
                        color: 'var(--tt-text-secondary)',
                        textAlign: 'center',
                        marginBottom: 40
                    }}
                >
                    We&apos;ve sent a sign-in link to your email address. It expires in 15 minutes.
                </p>

                <Link
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: 52,
                        borderRadius: 10,
                        background: 'var(--tt-accent)',
                        color: '#fff',
                        fontSize: 15.5,
                        fontWeight: 700,
                        textDecoration: 'none',
                        transition: 'background-color 0.2s ease',
                        fontFamily: 'inherit'
                    }}
                    to={loginUrl}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--tt-accent-hover)'; 
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--tt-accent)'; 
                    }}
                >
                    Back to sign in &rarr;
                </Link>
            </div>
        );
    }

    return (
        <div style={{width: '100%'}}>
            <header style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                <TribeTaleMark size={56} />
                <h1 style={{
                    fontSize: 30,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    color: 'var(--tt-text-primary)',
                    textAlign: 'center',
                    margin: '22px 0 40px',
                    padding: 0
                }}>
                    Sign in with a link
                </h1>
            </header>

            <p
                style={{
                    fontSize: 15,
                    lineHeight: 1.5,
                    color: 'var(--tt-text-secondary)',
                    textAlign: 'center',
                    marginBottom: 40
                }}
            >
                Enter your email address and we&apos;ll send you a link that signs you in — no password needed.
            </p>

            <form noValidate onSubmit={handleSubmit(onSubmit)}>
                <div style={{marginBottom: '1.5rem'}}>
                    <label
                        htmlFor="email"
                        style={{
                            display: 'block',
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--tt-text-primary)',
                            marginBottom: 7
                        }}
                    >
                        Work email
                    </label>
                    <input
                        {...register('email')}
                        autoCapitalize="off"
                        autoComplete="username"
                        autoCorrect="off"
                        id="email"
                        placeholder="jamie@example.com"
                        style={{
                            width: '100%',
                            height: 52,
                            padding: '0 16px',
                            borderRadius: 10,
                            fontSize: 15,
                            background: 'var(--tt-input-bg)',
                            border: '1px solid var(--tt-border)',
                            color: 'var(--tt-text-primary)',
                            outline: 'none',
                            transition: 'border-color .15s linear, box-shadow .15s linear',
                            boxSizing: 'border-box',
                            fontFamily: 'inherit'
                        }}
                        type="email"
                        onBlur={(e) => {
                            e.target.style.borderColor = 'var(--tt-border)';
                            e.target.style.boxShadow = 'none';
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = 'var(--tt-accent)';
                            e.target.style.boxShadow = '0 0 0 3px rgba(239,90,43,.25)';
                        }}
                    />
                    {errors.email && (
                        <p style={{marginTop: 4, fontSize: '1.3rem', color: 'var(--tt-red)'}}>{errors.email.message}</p>
                    )}
                </div>

                <button
                    disabled={isLoading}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        height: 52,
                        marginTop: 40,
                        borderRadius: 10,
                        border: 'none',
                        background: 'var(--tt-accent)',
                        color: '#fff',
                        fontSize: 15.5,
                        fontWeight: 700,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.6 : 1,
                        transition: 'background-color 0.2s ease',
                        fontFamily: 'inherit'
                    }}
                    type="submit"
                    onMouseEnter={(e) => {
                        if (!isLoading) {
                            (e.target as HTMLButtonElement).style.backgroundColor = 'var(--tt-accent-hover)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isLoading) {
                            (e.target as HTMLButtonElement).style.backgroundColor = 'var(--tt-accent)';
                        }
                    }}
                >
                    {isLoading ? (
                        <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8}}>
                            <svg style={{animation: 'spin 1s linear infinite', width: 18, height: 18}} viewBox="0 0 24 24">
                                <circle cx="12" cy="12" fill="none" r="10" stroke="currentColor" strokeWidth="4" style={{opacity: 0.25}} />
                                <path d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor" style={{opacity: 0.75}} />
                            </svg>
                            Sending...
                        </span>
                    ) : (
                        <>Send sign-in link &rarr;</>
                    )}
                </button>
            </form>

            {error && (
                <p style={{marginTop: 16, color: 'var(--tt-red)', fontSize: '1.4rem', lineHeight: 1.5, textAlign: 'center'}}>
                    {error}
                </p>
            )}

            <p style={{marginTop: 24, textAlign: 'center'}}>
                <Link
                    style={{
                        fontSize: 14,
                        color: 'var(--tt-text-secondary)',
                        textDecoration: 'none',
                        transition: 'color .15s linear'
                    }}
                    to={loginUrl}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--tt-text-primary)'; 
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--tt-text-secondary)'; 
                    }}
                >
                    &larr; Back to sign in
                </Link>
            </p>
        </div>
    );
}
